import type { IntentInstallOptions } from '@wix/editor-platform-host-integration-apis';
import { t } from '../../../essentials/i18n';
import { getGUID } from '../../utils/guid';
import { getOrigin, getOriginInfoType } from './helpers';
import {
  MarketListing,
  Status,
} from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/types';

export type IntentApp = { appDefId: string; version?: string };

type StoreConstructorParams = {
  options: IntentInstallOptions;
  appDefinitionIds: string[];
  intentApp?: IntentApp;
};

export class IntentInstallStore {
  private isInstallRunning: boolean = false;
  private installationId: string = getGUID();
  private options: IntentInstallOptions;
  private appNames: Record<string, string> = {};
  private appMarketListings: Record<string, MarketListing> = {};
  private appsToInstall: string[] = [];
  private appsWithErrors: string[] = [];
  private intentApp: IntentApp | undefined;

  private widgetsConfiguration: Record<string, any> = {};

  constructor({
    appDefinitionIds,
    options,
    intentApp,
  }: StoreConstructorParams) {
    this.appsToInstall = appDefinitionIds;
    this.options = options;
    this.intentApp = intentApp;
  }

  public getIsSingleApp() {
    return this.appsToInstall.length === 1;
  }

  public getAppsToInstall() {
    return this.appsToInstall;
  }

  public getIntentApp() {
    return this.intentApp;
  }

  public getOriginType() {
    const origin = getOrigin(this.options);
    return origin?.info?.type ?? getOriginInfoType(this.isSilent());
  }

  public isSilent() {
    return !!this.options?.isSilent;
  }

  public isRunning() {
    return this.isInstallRunning;
  }

  public setRunning(value: boolean) {
    this.isInstallRunning = value;
  }

  public getInstallationId() {
    return this.installationId;
  }

  public setAppMarketListings(apps?: Record<string, MarketListing>) {
    if (!apps) {
      return;
    }
    this.appMarketListings = apps;
  }

  public getAppName(appDefinitionId: string) {
    // unknown app should never happen, but just in case
    return (
      this.appMarketListings[appDefinitionId]?.basicInfo?.name ??
      t('Intent_Install_Unknown_App')
    );
  }

  public isAppPublished(appDefinitionId: string) {
    return this.appMarketListings[appDefinitionId]?.status === Status.PUBLISHED;
  }

  public setWidgetsConfiguration(widgetsConfiguration: Record<string, string>) {
    this.widgetsConfiguration = widgetsConfiguration;
  }

  public getWidgetsConfiguration() {
    return this.widgetsConfiguration;
  }

  public getAppsWithErrors() {
    return this.appsWithErrors;
  }
  public getAppsWithErrorsNames() {
    return this.appsWithErrors.map((appDefId) => this.appNames[appDefId]);
  }

  public addAppWithError(appDefId: string) {
    this.appsWithErrors.push(appDefId);
  }

  public getAppNamesToInstallExceptIntentApp() {
    const intentApp = this.getIntentApp();

    return this.getAppsToInstall()
      .filter((app) => app !== intentApp?.appDefId)
      .map((app) => this.getAppName(app));
  }

  public getIntentAppName() {
    const intentApp = this.getIntentApp();

    if (!intentApp) {
      return null;
    }

    return this.getAppName(intentApp.appDefId);
  }
}
